import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuoteModal from '../../../components/feature/QuoteModal';
import ProductCard from './ProductCard';
import { allProducts } from '../../../mocks/products';

interface ProductGridProps {
  selectedCategory: string;
  searchQuery: string;
}

export default function ProductGrid({ selectedCategory, searchQuery }: ProductGridProps) {
  const { t } = useTranslation('common');
  const [displayCount, setDisplayCount] = useState(12);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('');

  const filtered = allProducts.filter((p) => {
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.brand.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const visible = filtered.slice(0, displayCount);
  const hasMore = filtered.length > displayCount;

  const handleRequestQuote = (name: string) => {
    setSelectedProduct(name);
    setIsQuoteModalOpen(true);
  };

  return (
    <div>
      {/* Results Count */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">
          <span className="font-semibold text-gray-800">{filtered.length}</span>{' '}
          {t('products.productsFound')}
        </p>
      </div>

      {/* Grid */}
      {filtered.length > 0 ? (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5"
          data-product-shop
        >
          {visible.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onRequestQuote={handleRequestQuote}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
            <i className="ri-search-line text-3xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">{t('products.noResults')}</h3>
          <p className="text-sm text-gray-500">{t('products.noResultsDesc')}</p>
        </div>
      )}

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center mt-10">
          <button
            onClick={() => setDisplayCount((prev) => prev + 8)}
            className="flex items-center gap-2 border border-gray-200 bg-white text-gray-700 px-8 py-3 rounded-full text-sm font-semibold hover:border-teal-500 hover:text-teal-600 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line"></i>
            {t('products.loadMore')}
          </button>
        </div>
      )}

      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        productName={selectedProduct}
      />
    </div>
  );
}
